import Box from '@mui/material/Box';
import { type ForwardedRef, forwardRef, type ReactNode, type JSX } from 'react';
import {
  FormMessage,
  shouldRenderFormMessage,
  type FormMessageProps,
} from '../formMessage/formMessage';
import { FormSubText } from '../formSubText/formSubText';
import { useTheme } from '../../../../theme';
import { FormLabel, FormLabelProps } from '../formLabel/formLabel';
import { Link } from '../../link';

export type FormElementProps = FormMessageProps &
  FormLabelProps & {
    children?: ReactNode;
    subText?: {
      text: string;
      tooltip?: string;
    };
    actionLink?: {
      iconComponent?: ReactNode;
      text: string;
      onClick: () => void;
    };
    rightComponent?: ReactNode;
  };

/**
 * Wraps inputs and provides a series of informational labels & components.
 */
export const FormElement = forwardRef(
  (props: FormElementProps, ref: ForwardedRef<HTMLElement>): JSX.Element => {
    const {
      label,

      // FormLabel props
      htmlFor,
      labelId,
      labelTooltip,
      labelBadge,
      required,
      labelTooltipPosition,
      labelVisuallyHidden,

      children,

      subText,
      actionLink,
      rightComponent,

      ...formMessageProps
    } = props;

    const theme = useTheme();

    const shouldRenderMessage = shouldRenderFormMessage(formMessageProps);

    return (
      <Box
        sx={{
          maxWidth: 480,
        }}
      >
        {(Boolean(label) || Boolean(rightComponent)) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              // Only add the margin if there's any immediate siblings and the label is actually occupying space.
              ...((children || shouldRenderMessage) &&
                !labelVisuallyHidden && {
                  marginBottom: theme.spacing(1),
                }),
            }}
            data-testid='form-element-label-container'
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1),
                alignSelf: 'center',
              }}
            >
              <FormLabel
                label={label}
                labelId={labelId}
                labelTooltip={labelTooltip}
                labelTooltipPosition={labelTooltipPosition}
                labelBadge={labelBadge}
                htmlFor={htmlFor}
                required={required}
                labelVisuallyHidden={labelVisuallyHidden}
              />

              {/** Treat visually hidden as not present to align to previous behaviour where this wouldn't render without a label */}
              {!labelVisuallyHidden && (
                <FormSubText
                  infoIconTitle={subText?.tooltip}
                  subText={subText?.text}
                />
              )}
            </Box>

            {/** Treat visually hidden as not present to align to previous behaviour where this wouldn't render without a label */}
            {label !== undefined && !labelVisuallyHidden && rightComponent}
          </Box>
        )}
        {children && (
          <>
            {/*
            This Box wraps the children and applies a default width of 100% to all immediate children.

            1.The parent Box applied '& > * { width: 100% }' via the CSS prop.
              CSS injection order (last style in stylesheet wins) ensured child-defined widths could override this.

            2. If we introduced computed styles (like margin-bottom), the order of CSS injection
              would change. This caused the parent's width rule to override children unexpectedly.

            3. To avoid breaking the CSS injection order:
              - We keep the '& > *' rule for default widths.
              - We use a Spacer (Box with margin-bottom) instead of relying on the parent Box's margin
                so computed spacing doesn't interfere with child widths.

            Note: Ideally, the parent should not force child widths given we cannot control specificity, 
            but this preserves previous layout behavior.
          */}
            <Box
              css={{
                '& > *': {
                  width: '100%',
                },
              }}
              ref={ref}
            >
              {children}
            </Box>
            {shouldRenderMessage && (
              <Box
                data-testid='form-element-children-spacer'
                mb={theme.spacing(1)}
              />
            )}
          </>
        )}
        {shouldRenderFormMessage(formMessageProps) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexDirection: 'column',
              gap: 1,
            }}
            data-testid='form-message'
          >
            <FormMessage {...formMessageProps} />

            {actionLink !== undefined && (
              <Link
                onClick={actionLink.onClick}
                startIcon={actionLink.iconComponent}
              >
                {actionLink.text}
              </Link>
            )}
          </Box>
        )}
      </Box>
    );
  },
);
